import { httpClient, ReadService, type HttpClient } from '../core'
import { templateVersionsEndpoints } from './template-versions.endpoints'
import { createTemplateVersionResponseSchema, templateVersionPageSchema, templateVersionSchema, type GenerateVariationBody, type CreateTemplateVersionResponseDto, type TemplateVersionDto, type TemplateVersionFilters } from './template-versions.schemas'

/** Reads a project's template versions (`GET /api/v1/template-versions?projectId=…`) and makes new ones and starts new ones (`POST /api/projects/{id}/templates/generate-variations`). */
export class TemplateVersionsService extends ReadService<TemplateVersionDto, TemplateVersionFilters> {
  constructor(http: HttpClient = httpClient) {
    super(http, templateVersionsEndpoints, { item: templateVersionSchema, page: templateVersionPageSchema })
  }

  /**
   * Starts one variation of an approved template. The server stores it as a draft with the creator, the content and the instructions,
   * queues the task that draws it, and answers with both: follow the task through its event stream. Never retried: a lost request may
   * still have worked, and a second one would make a second variation.
   */
  generate(projectId: string, body: GenerateVariationBody, signal?: AbortSignal): Promise<CreateTemplateVersionResponseDto> {
    const instructions = body.instructions?.trim()
    return this.http.post(templateVersionsEndpoints.generateVariations(projectId), createTemplateVersionResponseSchema, { body: { createdBy: body.createdBy, content: body.content, imageUrl: body.imageUrl, ...(instructions && { instructions }) }, signal, timeoutMs: 2 * 60_000 })
  }

  /** Every variation of one project, newest first. */
  async ofProject(projectId: string, filters: Omit<TemplateVersionFilters, 'projectId'> = {}, signal?: AbortSignal): Promise<TemplateVersionDto[]> {
    const items = await this.listAll({ ...filters, projectId }, signal)
    return items.toSorted((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
  }
}

export const templateVersionsService = new TemplateVersionsService()
